import { useState, useCallback, useEffect, useRef } from 'react';
import Select, { SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import { projectsApi } from '../../utils/projectsApi';
import styles from './MoveToProjectModal.module.scss';

interface Project {
  id: number;
  name: string;
  case_ids?: number[];
}

interface ProjectOption {
  label: string;
  value: number;
}

interface MoveToProjectModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: (result: { caseId: number; targetProjectId: number }) => void;
  caseId: number | null;
  sourceProjectId: number | null;
  projects: Project[];
}

export default function MoveToProjectModal({
  isVisible,
  onClose,
  onSuccess,
  caseId,
  sourceProjectId,
  projects,
}: MoveToProjectModalProps) {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = bodyRef.current;
    if (!container) return;

    if (menuOpen) {
      const menu = container.querySelector<HTMLElement>('.react-select__menu');
      if (menu) {
        container.style.height = `${container.offsetHeight + menu.offsetHeight}px`;
      }
    } else {
      container.style.height = '';
    }
  }, [menuOpen]);

  const options: ProjectOption[] = projects
    .filter((p) => p.id !== sourceProjectId)
    .map((p) => ({ label: p.name, value: p.id }));

  const canSubmit = Boolean(selectedProject && caseId && !isSubmitting);

  const handleClose = useCallback(() => {
    setSelectedProject(null);
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!selectedProject || caseId === null) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await projectsApi.moveCase(caseId, selectedProject.value);
      toast.success(t('Case moved to {{project}}', { project: selectedProject.label }) as string);
      onSuccess({ caseId, targetProjectId: selectedProject.value });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedProject, caseId, onSuccess, handleClose, t]);

  const content = (
    <div ref={bodyRef} className={styles.body}>
      <div className={styles.field}>
        <label htmlFor="move-project-select" className={styles.label}>
          {t('Project')}
        </label>
        <Select<ProjectOption>
          inputId="move-project-select"
          options={options}
          value={selectedProject}
          onChange={(option: SingleValue<ProjectOption>) => setSelectedProject(option)}
          placeholder={t('Select project')}
          classNamePrefix="react-select"
          aria-label={t('Select project')}
          isDisabled={isSubmitting}
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          components={{ IndicatorSeparator: () => null, ClearIndicator: () => null }}
        />
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <button
        type="button"
        className="btn-reset"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        {t('Cancel')}
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {isSubmitting ? t('Moving…') : t('Move to project')}
      </button>
    </div>
  );

  return (
    <Modal
      isVisible={isVisible}
      title={t('Move to project')}
      content={content}
      footer={footer}
      onClose={handleClose}
      doNotCloseOnOutsideClick={true}
      className={styles.modal}
    />
  );
}
